import React from "react";
import * as Yup from "yup";
import { isNullOrWhiteSpace } from "../../../common/helper/helper";
import { formFieldRex } from "../../GenericForm/Components/FunctionLib";
import { IFormSelectOption, MembershipFormType, WaFormFieldConstants } from "./Constants";

const postcodeRegExp = /^(?:(?:[2-8]\d|9[0-7]|0?[28]|0?9(?=09))(?:\d{2}))$/;
const phoneRegExp =
	/^[+]?(?=(?:[^\dx]*\d){7})(?:\(\d+(?:\.\d+)?\)|\d+(?:\.\d+)?)(?:[ -]?(?:\(\d+(?:\.\d+)?\)|\d+(?:\.\d+)?))*(?:[ ]?(?:x|ext)\.?[ ]?\d{1,5})?$/;

export const WaMembershipValiationSchemaFactory = (formType: MembershipFormType) => {
	switch (formType) {
		case MembershipFormType.Join:
			return createJoinMembershipValidationSchema;
		case MembershipFormType.Renew:
			return createRenewalMembershipValidationSchema;
		case MembershipFormType.Update:
			return createUpdateMembershipValidationSchema;
	}
};

const baseValidationSchema = {
	[WaFormFieldConstants.firstName]: Yup.string().required("First name is required").max(40),
	[WaFormFieldConstants.lastName]: Yup.string().required("Surname is required").max(40),
	[WaFormFieldConstants.email]: Yup.string()
		.required("Email is required")
		.email("This field must be a valid email address")
		.max(50),
	[WaFormFieldConstants.address]: Yup.string().required("Address is required").max(50),
	[WaFormFieldConstants.suburb]: Yup.string().required("Suburb is required").max(50),
	[WaFormFieldConstants.state]: Yup.string().required("State is required"),
	[WaFormFieldConstants.adminComRefDetails]: Yup.string()
		.notRequired()
		.when(
			[
				WaFormFieldConstants.adminComRef1,
				WaFormFieldConstants.adminComRef2,
				WaFormFieldConstants.adminComRef3,
				WaFormFieldConstants.adminComRef4,
				WaFormFieldConstants.adminComRef5,
				WaFormFieldConstants.adminComRef6,
			],
			{
				is: (a, b, c, d, e, f) =>
					a === true ||
					b === true ||
					c === true ||
					d === true ||
					e === true ||
					f === true,
				then: Yup.string().required("Please provide details"),
			}
		),
};

const createJoinMembershipValidationSchema = () => {
	const schema = Object.assign(baseValidationSchema, {
		[WaFormFieldConstants.gender]: Yup.string().required("Gender is required"),
		[WaFormFieldConstants.occupation]: Yup.string().required("Occupation is required").max(50),
		[WaFormFieldConstants.dateOfBirth]: Yup.string()
			.required("Date of birth is required")
			.matches(formFieldRex.dateRegExp, "This field must be a valid date"),
		[WaFormFieldConstants.phone]: Yup.string()
			.required("Phone number is required")
			.matches(phoneRegExp, "This field must be a valid phone number"),
		[WaFormFieldConstants.electoralRoll]: Yup.string().required(
			"Please specify whether you are on the electoral roll"
		),
		[WaFormFieldConstants.aboriginalDescent]: Yup.string().required(
			"Please specify whether you identify as Aboriginal or Torres Strait Islander"
		),
		[WaFormFieldConstants.sameAsResidential]: Yup.string().required(
			"Please specify whether your postal address is the same as your residential address"
		),
		[WaFormFieldConstants.postalAddress]: Yup.string()
			.notRequired()
			.when(
				[
					WaFormFieldConstants.sameAsResidential,
					WaFormFieldConstants.address,
					WaFormFieldConstants.state,
					WaFormFieldConstants.postcode,
					WaFormFieldConstants.suburb,
				],
				{
					is: (a, b, c, d) =>
						a === "no" &&
						(!isNullOrWhiteSpace(b) ||
							!isNullOrWhiteSpace(c) ||
							!isNullOrWhiteSpace(d)),
					then: Yup.string()
						.required(getErroMsgForAddres(WaFormFieldConstants.address, true))
						.max(50),
				}
			),
		[WaFormFieldConstants.postalSuburb]: Yup.string()
			.notRequired()
			.when(
				[
					WaFormFieldConstants.sameAsResidential,
					WaFormFieldConstants.address,
					WaFormFieldConstants.state,
					WaFormFieldConstants.postcode,
					WaFormFieldConstants.suburb,
				],
				{
					is: (a, b, c, d) =>
						a === "no" &&
						(!isNullOrWhiteSpace(b) ||
							!isNullOrWhiteSpace(c) ||
							!isNullOrWhiteSpace(d)),
					then: Yup.string()
						.required(getErroMsgForAddres(WaFormFieldConstants.suburb, true))
						.max(50),
				}
			),
		[WaFormFieldConstants.postalState]: Yup.string()
			.notRequired()
			.when(
				[
					WaFormFieldConstants.sameAsResidential,
					WaFormFieldConstants.address,
					WaFormFieldConstants.state,
					WaFormFieldConstants.postcode,
					WaFormFieldConstants.suburb,
				],
				{
					is: (a, b, c, d) =>
						a === "no" &&
						(!isNullOrWhiteSpace(b) ||
							!isNullOrWhiteSpace(c) ||
							!isNullOrWhiteSpace(d)),
					then: Yup.string()
						.required(getErroMsgForAddres(WaFormFieldConstants.state, true))
						.max(50),
				}
			),
		[WaFormFieldConstants.postalPostcode]: Yup.string()
			.notRequired()
			.when(
				[
					WaFormFieldConstants.sameAsResidential,
					WaFormFieldConstants.address,
					WaFormFieldConstants.state,
					WaFormFieldConstants.postcode,
					WaFormFieldConstants.suburb,
				],
				{
					is: (a, b, c, d) =>
						a === "no" &&
						(!isNullOrWhiteSpace(b) ||
							!isNullOrWhiteSpace(c) ||
							!isNullOrWhiteSpace(d)),
					then: Yup.string()
						.required(getErroMsgForAddres(WaFormFieldConstants.postcode, true))
						.matches(postcodeRegExp, "This field must be a valid postcode")
						.max(4),
				}
			),
		[WaFormFieldConstants.nominatedBranch]: Yup.string()
			.required("Please nominate a branch")
			.max(50),
		[WaFormFieldConstants.signature]: Yup.string().required("Signature is required"),
		[WaFormFieldConstants.annualIncome]: Yup.string().required(
			"Please select your annual income"
		),
		[WaFormFieldConstants.application]: Yup.bool().oneOf(
			[true],
			"Please tick the application box"
		),
		[WaFormFieldConstants.declaration]: Yup.bool().oneOf(
			[true],
			"Please tick the declaration box"
		),
	});

	return Yup.object().shape(schema, [
		[WaFormFieldConstants.address, WaFormFieldConstants.suburb],
		[WaFormFieldConstants.address, WaFormFieldConstants.state],
		[WaFormFieldConstants.address, WaFormFieldConstants.postcode],
		[WaFormFieldConstants.suburb, WaFormFieldConstants.state],
		[WaFormFieldConstants.suburb, WaFormFieldConstants.postcode],
		[WaFormFieldConstants.state, WaFormFieldConstants.postcode],
		[WaFormFieldConstants.postalAddress, WaFormFieldConstants.postalSuburb],
		[WaFormFieldConstants.postalAddress, WaFormFieldConstants.postalState],
		[WaFormFieldConstants.postalAddress, WaFormFieldConstants.postalPostcode],
		[WaFormFieldConstants.postalSuburb, WaFormFieldConstants.postalState],
		[WaFormFieldConstants.postalSuburb, WaFormFieldConstants.postalPostcode],
		[WaFormFieldConstants.postalState, WaFormFieldConstants.postalPostcode],
	]);
};

const createRenewalMembershipValidationSchema = () => {
	const schema = Object.assign(baseValidationSchema, {
		[WaFormFieldConstants.membershipNumber]: Yup.string().required(
			"Membership number is required"
		),
		[WaFormFieldConstants.phone]: Yup.string()
			.required("Phone number is required")
			.matches(phoneRegExp, "This field must be a valid phone number"),
		[WaFormFieldConstants.annualIncome]: Yup.string().required(
			"Please select your annual income"
		),
		[WaFormFieldConstants.sameAsResidential]: Yup.string().required(
			"Please specify whether your postal address is the same as your residential address"
		),
		[WaFormFieldConstants.signature]: Yup.string().required("Signature is required"),
		[WaFormFieldConstants.application]: Yup.bool().oneOf(
			[true],
			"Please tick the application box"
		),
		[WaFormFieldConstants.declaration]: Yup.bool().oneOf(
			[true],
			"Please tick the declaration box"
		),
	});

	return Yup.object().shape(schema);
};

const getErroMsgForAddres = (fieldName: string, postal?: boolean) =>
	`Please complete ${fieldName} for the ${postal ? "postal" : "residential"} address`;

const createUpdateMembershipValidationSchema = () => {
	//have to create a seperate schema for update member since all fields are not required except the memberNum and dob
	const updateMemberschema = {
		[WaFormFieldConstants.firstName]: Yup.string().notRequired().max(40),
		[WaFormFieldConstants.lastName]: Yup.string().notRequired().max(40),
		[WaFormFieldConstants.email]: Yup.string()
			.notRequired()
			.email("This field must be a valid email address")
			.max(50),
		[WaFormFieldConstants.address]: Yup.string()
			.notRequired()
			.when(
				[
					WaFormFieldConstants.suburb,
					WaFormFieldConstants.state,
					WaFormFieldConstants.postcode,
				],
				{
					is: (a, b, c) =>
						!isNullOrWhiteSpace(a) || !isNullOrWhiteSpace(b) || !isNullOrWhiteSpace(c),
					then: Yup.string()
						.required(getErroMsgForAddres(WaFormFieldConstants.address))
						.max(50),
				}
			),

		[WaFormFieldConstants.suburb]: Yup.string()
			.notRequired()
			.nullable()
			.when(
				[
					WaFormFieldConstants.address,
					WaFormFieldConstants.state,
					WaFormFieldConstants.postcode,
				],
				{
					is: (a, b, c) =>
						!isNullOrWhiteSpace(a) || !isNullOrWhiteSpace(b) || !isNullOrWhiteSpace(c),
					then: Yup.string()
						.required(getErroMsgForAddres(WaFormFieldConstants.suburb))
						.max(50),
				}
			),
		[WaFormFieldConstants.state]: Yup.string()
			.notRequired()
			.nullable()
			.when(
				[
					WaFormFieldConstants.address,
					WaFormFieldConstants.suburb,
					WaFormFieldConstants.postcode,
				],
				{
					is: (a, b, c) =>
						!isNullOrWhiteSpace(a) || !isNullOrWhiteSpace(b) || !isNullOrWhiteSpace(c),
					then: Yup.string()
						.required(getErroMsgForAddres(WaFormFieldConstants.state))
						.max(50),
				}
			),
		[WaFormFieldConstants.postcode]: Yup.string()
			.notRequired()
			.nullable()
			.when(
				[
					WaFormFieldConstants.address,
					WaFormFieldConstants.state,
					WaFormFieldConstants.suburb,
				],
				{
					is: (a, b, c) =>
						!isNullOrWhiteSpace(a) || !isNullOrWhiteSpace(b) || !isNullOrWhiteSpace(c),
					then: Yup.string()
						.required(getErroMsgForAddres(WaFormFieldConstants.postcode))
						.matches(postcodeRegExp, "This field must be a valid postcode")
						.max(4),
				}
			),
		[WaFormFieldConstants.postalAddress]: Yup.string()
			.notRequired()
			.when(
				[
					WaFormFieldConstants.sameAsResidential,
					WaFormFieldConstants.address,
					WaFormFieldConstants.state,
					WaFormFieldConstants.postcode,
					WaFormFieldConstants.suburb,
				],
				{
					is: (a, b, c, d) =>
						a === "no" &&
						(!isNullOrWhiteSpace(b) ||
							!isNullOrWhiteSpace(c) ||
							!isNullOrWhiteSpace(d)),
					then: Yup.string()
						.required(getErroMsgForAddres(WaFormFieldConstants.address, true))
						.max(50),
				}
			),
		[WaFormFieldConstants.postalSuburb]: Yup.string()
			.notRequired()
			.when(
				[
					WaFormFieldConstants.sameAsResidential,
					WaFormFieldConstants.address,
					WaFormFieldConstants.state,
					WaFormFieldConstants.postcode,
					WaFormFieldConstants.suburb,
				],
				{
					is: (a, b, c, d) =>
						a === "no" &&
						(!isNullOrWhiteSpace(b) ||
							!isNullOrWhiteSpace(c) ||
							!isNullOrWhiteSpace(d)),
					then: Yup.string()
						.required(getErroMsgForAddres(WaFormFieldConstants.suburb, true))
						.max(50),
				}
			),
		[WaFormFieldConstants.postalState]: Yup.string()
			.notRequired()
			.when(
				[
					WaFormFieldConstants.sameAsResidential,
					WaFormFieldConstants.address,
					WaFormFieldConstants.state,
					WaFormFieldConstants.postcode,
					WaFormFieldConstants.suburb,
				],
				{
					is: (a, b, c, d) =>
						a === "no" &&
						(!isNullOrWhiteSpace(b) ||
							!isNullOrWhiteSpace(c) ||
							!isNullOrWhiteSpace(d)),
					then: Yup.string()
						.required(getErroMsgForAddres(WaFormFieldConstants.state, true))
						.max(50),
				}
			),
		[WaFormFieldConstants.postalPostcode]: Yup.string()
			.notRequired()
			.when(
				[
					WaFormFieldConstants.sameAsResidential,
					WaFormFieldConstants.address,
					WaFormFieldConstants.state,
					WaFormFieldConstants.postcode,
					WaFormFieldConstants.suburb,
				],
				{
					is: (a, b, c, d) =>
						a === "no" &&
						(!isNullOrWhiteSpace(b) ||
							!isNullOrWhiteSpace(c) ||
							!isNullOrWhiteSpace(d)),
					then: Yup.string()
						.required(getErroMsgForAddres(WaFormFieldConstants.postcode, true))
						.matches(postcodeRegExp, "This field must be a valid postcode")
						.max(4),
				}
			),

		[WaFormFieldConstants.membershipNumber]: Yup.string().required(
			"Membership number is required"
		),
		[WaFormFieldConstants.gender]: Yup.string(),
		[WaFormFieldConstants.occupation]: Yup.string().notRequired().max(50),
		[WaFormFieldConstants.dateOfBirth]: Yup.string()
			.required("Date of birth is required")
			.matches(formFieldRex.dateRegExp, "This field must be a valid date"),
		[WaFormFieldConstants.phone]: Yup.string()
			.notRequired()
			.matches(phoneRegExp, "This field must be a valid phone number"),
	};
	return Yup.object().shape(updateMemberschema, [
		[WaFormFieldConstants.address, WaFormFieldConstants.suburb],
		[WaFormFieldConstants.address, WaFormFieldConstants.state],
		[WaFormFieldConstants.address, WaFormFieldConstants.postcode],
		[WaFormFieldConstants.suburb, WaFormFieldConstants.state],
		[WaFormFieldConstants.suburb, WaFormFieldConstants.postcode],
		[WaFormFieldConstants.state, WaFormFieldConstants.postcode],
		[WaFormFieldConstants.postalAddress, WaFormFieldConstants.postalSuburb],
		[WaFormFieldConstants.postalAddress, WaFormFieldConstants.postalState],
		[WaFormFieldConstants.postalAddress, WaFormFieldConstants.postalPostcode],
		[WaFormFieldConstants.postalSuburb, WaFormFieldConstants.postalState],
		[WaFormFieldConstants.postalSuburb, WaFormFieldConstants.postalPostcode],
		[WaFormFieldConstants.postalState, WaFormFieldConstants.postalPostcode],
	]);
};

export const FirstLetterUpperCase = (letter: string) => {
	return letter.charAt(0).toUpperCase() + letter.slice(1);
};

export const renderOptions = (opt: IFormSelectOption, index: number) => (
	<option key={index} value={opt.value}>
		{opt.text}
	</option>
);
